import { z } from 'zod';
import { documentContentSchema } from '../content/schema.js';
import { CLASSIFICATIONS, EXPORT_FORMATS, REVIEW_DECISIONS, TENANT_ROLES } from '../domain/enums.js';
import { pdfThemeConfigSchema } from '../domain/pdf-theme.js';

export const emailSchema = z.string().trim().toLowerCase().email().max(320);
export const passwordSchema = z
  .string()
  .min(12, 'Password must be at least 12 characters')
  .max(200)
  .refine((v) => /[a-z]/.test(v) && /[A-Z]/.test(v) && /[0-9]/.test(v), {
    message: 'Password must contain upper case, lower case and a number',
  });

export const tenantSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(3)
  .max(40)
  .regex(/^[a-z0-9][a-z0-9-]*[a-z0-9]$/, 'Use lower case letters, numbers and hyphens');

/* ── Auth ─────────────────────────────────────────────────────────────── */

export const registerSchema = z.object({
  organisationName: z.string().trim().min(2).max(120),
  organisationSlug: tenantSlugSchema.optional(),
  name: z.string().trim().min(1).max(120),
  email: emailSchema,
  password: passwordSchema,
});

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
  tenantSlug: z.string().trim().toLowerCase().max(40).optional(),
});

export const acceptInviteSchema = z.object({
  token: z.string().min(10).max(200),
  name: z.string().trim().min(1).max(120).optional(),
  password: passwordSchema.optional(),
});

export const changePasswordSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: passwordSchema,
});

/* ── Tenancy ──────────────────────────────────────────────────────────── */

export const inviteMemberSchema = z.object({
  email: emailSchema,
  role: z.enum(TENANT_ROLES),
});

export const updateMemberSchema = z.object({
  role: z.enum(TENANT_ROLES),
});

export const updateTenantSchema = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  settings: z
    .object({
      referencePadding: z.number().int().min(1).max(6).optional(),
      defaultClassification: z.enum(CLASSIFICATIONS).optional(),
      reviewIntervalMonths: z.number().int().min(1).max(60).optional(),
      requireChangeRequestForPublished: z.boolean().optional(),
      branding: z
        .object({
          title: z.string().max(120).nullable().optional(),
          tagline: z.string().max(240).nullable().optional(),
          // Inline images only: a URL would let the portal fetch from elsewhere.
          logoDataUri: z
            .string()
            .max(2_000_000)
            .regex(/^data:image\/(png|jpeg|jpg|gif|webp|svg\+xml);base64,[A-Za-z0-9+/=\s]+$/, 'Upload a PNG, JPEG, GIF, WebP or SVG image')
            .nullable()
            .optional(),
          accentColor: z
            .string()
            .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, 'Use a hex colour such as #2f34c9')
            .optional(),
          supportEmail: z.string().max(320).email().nullable().optional(),
        })
        .optional(),
      ai: z
        .object({
          enabled: z.boolean().optional(),
          /* Null clears the override and falls back to the server's own setting. */
          provider: z.enum(['openai', 'azure-openai', 'none']).nullable().optional(),
          model: z.string().max(80).nullable().optional(),
          apiKey: z.string().max(300).nullable().optional(),
          /*
           * The server issues an authenticated request to this address, so it
           * must be an https URL on a public network. The address itself is
           * re-checked server-side before it is saved and again before use.
           */
          baseUrl: z
            .string()
            .max(300)
            .refine((value) => value === '' || /^https:\/\/[^\s/$.?#].[^\s]*$/i.test(value), {
              message: 'The base URL must be an https address',
            })
            .nullable()
            .optional(),
        })
        .optional(),
    })
    .optional(),
});

/* ── Documents ────────────────────────────────────────────────────────── */

export const documentMetadataSchema = z.record(z.union([z.string(), z.array(z.string()), z.number(), z.boolean(), z.null()]));

export const createDocumentSchema = z.object({
  title: z.string().trim().min(1).max(300),
  documentTypeKey: z.string().trim().min(1).max(60),
  templateId: z.string().max(64).nullable().optional(),
  reference: z.string().trim().max(40).optional(),
  summary: z.string().max(2000).optional(),
  metadata: documentMetadataSchema.optional(),
  /** Import Markdown instead of starting from the template. */
  importMarkdown: z.string().max(2_000_000).optional(),
});

export const updateDocumentSchema = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  summary: z.string().max(2000).nullable().optional(),
  ownerId: z.string().max(64).nullable().optional(),
  reference: z.string().trim().max(40).optional(),
  metadata: documentMetadataSchema.optional(),
});

export const saveDraftSchema = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  content: documentContentSchema,
  metadata: documentMetadataSchema.optional(),
  changeSummary: z.string().max(1000).optional(),
  /** Optimistic concurrency: the version the client started editing from. */
  baseVersionId: z.string().max(64).optional(),
});

export const publishSchema = z.object({
  versionBump: z.enum(['major', 'minor']).default('minor'),
  changeSummary: z.string().max(1000).optional(),
  effectiveFrom: z.string().max(30).nullable().optional(),
  reviewDue: z.string().max(30).nullable().optional(),
});

export const listDocumentsQuerySchema = z.object({
  q: z.string().max(200).optional(),
  status: z.string().max(40).optional(),
  typeKey: z.string().max(60).optional(),
  ownerId: z.string().max(64).optional(),
  tag: z.string().max(60).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  sort: z.enum(['updated', 'created', 'title', 'reference', 'review']).default('updated'),
  order: z.enum(['asc', 'desc']).default('desc'),
});

/* ── Templates ────────────────────────────────────────────────────────── */

export const templateFieldSchema = z.object({
  key: z.string().trim().min(1).max(60).regex(/^[a-z][a-z0-9_]*$/, 'Use snake_case'),
  label: z.string().trim().min(1).max(120),
  type: z.enum(['text', 'textarea', 'date', 'select', 'tags', 'user']),
  required: z.boolean().optional(),
  options: z.array(z.string().max(80)).max(50).optional(),
  help: z.string().max(400).optional(),
  defaultValue: z.string().max(400).optional(),
});

export const upsertTemplateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(1000).optional(),
  documentTypeKey: z.string().trim().min(1).max(60),
  fields: z.array(templateFieldSchema).max(60).default([]),
  content: documentContentSchema,
  isDefault: z.boolean().optional(),
});

export const upsertDocumentTypeSchema = z.object({
  key: z.string().trim().min(1).max(60).regex(/^[a-z][a-z0-9_]*$/, 'Use snake_case'),
  name: z.string().trim().min(1).max(120),
  description: z.string().max(1000).optional(),
  referencePrefix: z.string().trim().min(1).max(8).regex(/^[A-Z][A-Z0-9]*$/, 'Use upper case letters'),
  sortOrder: z.number().int().min(0).max(1000).optional(),
});

/* ── Governance ───────────────────────────────────────────────────────── */

export const createChangeRequestSchema = z.object({
  documentId: z.string().min(1).max(64),
  title: z.string().trim().min(1).max(200),
  description: z.string().max(20000).optional(),
  /** Proposed content. Omit to open a change request against the current draft. */
  content: documentContentSchema.optional(),
  metadata: documentMetadataSchema.optional(),
  proposedTitle: z.string().trim().min(1).max(300).optional(),
  reviewerIds: z.array(z.string().max(64)).max(25).optional(),
  submit: z.boolean().default(false),
});

export const updateChangeRequestSchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  description: z.string().max(20000).nullable().optional(),
  content: documentContentSchema.optional(),
  proposedTitle: z.string().trim().min(1).max(300).optional(),
  metadata: documentMetadataSchema.optional(),
  reviewerIds: z.array(z.string().max(64)).max(25).optional(),
});

export const reviewChangeRequestSchema = z.object({
  decision: z.enum(REVIEW_DECISIONS),
  comment: z.string().max(10000).optional(),
});

export const mergeChangeRequestSchema = z.object({
  versionBump: z.enum(['major', 'minor']).default('minor'),
  publish: z.boolean().default(true),
  changeSummary: z.string().max(1000).optional(),
  effectiveFrom: z.string().max(30).nullable().optional(),
  reviewDue: z.string().max(30).nullable().optional(),
});

export const commentSchema = z.object({
  body: z.string().trim().min(1).max(10000),
  parentId: z.string().max(64).nullable().optional(),
  /** Anchors a comment to a section of the proposal. */
  anchor: z
    .object({
      sectionId: z.string().max(64).optional(),
      sectionAnchor: z.string().max(120).optional(),
      quote: z.string().max(500).optional(),
    })
    .nullable()
    .optional(),
});

export const approvalPolicySchema = z.object({
  documentTypeKey: z.string().max(60).nullable().optional(),
  minApprovals: z.number().int().min(0).max(20).default(1),
  requiredRoles: z.array(z.enum(TENANT_ROLES)).max(5).default(['admin']),
  allowSelfApproval: z.boolean().default(false),
  /** Block merging while any reviewer has requested changes. */
  blockOnRequestedChanges: z.boolean().default(true),
});

/* ── Export ───────────────────────────────────────────────────────────── */

/**
 * Query-string booleans.
 *
 * `z.coerce.boolean()` is not usable here: it applies JavaScript truthiness, so
 * the string "false" would coerce to `true`.
 */
export const booleanQuery = z
  .union([z.boolean(), z.string()])
  .transform((value) => (typeof value === 'boolean' ? value : ['true', '1', 'yes', 'on'].includes(value.toLowerCase())));

export const exportQuerySchema = z.object({
  format: z.enum(EXPORT_FORMATS),
  versionId: z.string().max(64).optional(),
  themeId: z.string().max(64).optional(),
  includeToc: booleanQuery.optional(),
  includeFrontMatter: booleanQuery.optional(),
  numberHeadings: booleanQuery.optional(),
});

export const upsertPdfThemeSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().max(500).optional(),
  config: pdfThemeConfigSchema,
  isDefault: z.boolean().optional(),
});

/* ── AI ───────────────────────────────────────────────────────────────── */

export const AI_ACTIONS = ['draft_section', 'rewrite', 'summarise', 'expand', 'shorten', 'suggest_sections', 'review'] as const;
export type AiAction = (typeof AI_ACTIONS)[number];

/**
 * Smart import: hand an existing policy document to the model and get back the
 * same document expressed in the house structure.
 *
 * The source is untrusted — it is somebody's Word file — so the caps here are
 * the outer bound, and the service applies its own before anything is sent.
 * The character cap matches what the server's chunking can actually carry: a
 * dozen calls' worth. Beyond it the source is truncated and the proposal says
 * so, rather than quietly proposing half a policy.
 */
export const AI_IMPORT_MAX_SOURCE_CHARS = 216_000;
/** Base64 inflates by a third, and the server's body limit is 8 MB. */
export const AI_IMPORT_MAX_FILE_BASE64 = 6_000_000;
export const AI_IMPORT_EXTENSIONS = ['.pdf', '.docx', '.md', '.markdown', '.txt'] as const;

/**
 * Drafting from a brief.
 *
 * The counterpart to smart import: instead of restructuring a document that
 * exists, produce one that does not. Same pipeline afterwards — the model
 * returns Markdown, our parser makes content of it, and a person reviews a
 * proposal before anything is created.
 */
export const aiDraftSchema = z.object({
  /** What the document is for, in the author's own words. */
  brief: z.string().trim().min(20, 'Describe what the document needs to cover').max(4000),
  documentTypeKey: z.string().trim().max(60).optional(),
  /** When set, the template's sections are the structure the draft must fill. */
  templateId: z.string().max(64).nullable().optional(),
  title: z.string().trim().max(300).optional(),
  tone: z.enum(['formal', 'plain', 'concise']).default('formal'),
});

export const aiImportSchema = z
  .object({
    /** Pasted text, when the source did not come from a file. */
    text: z.string().max(AI_IMPORT_MAX_SOURCE_CHARS).optional(),
    /** A .docx, .md or .txt file, base64 encoded. */
    fileBase64: z.string().max(AI_IMPORT_MAX_FILE_BASE64).optional(),
    filename: z.string().max(255).optional(),
    /** Steers the structure the model aims for; it never sets the document's type. */
    documentTypeKey: z.string().trim().max(60).optional(),
    instruction: z.string().max(2000).optional(),
  })
  .refine((value) => Boolean(value.text?.trim()) || Boolean(value.fileBase64), {
    message: 'Provide a file or some text to import',
  });

export const aiAssistSchema = z.object({
  action: z.enum(AI_ACTIONS),
  documentId: z.string().max(64).optional(),
  sectionId: z.string().max(64).optional(),
  sectionTitle: z.string().max(300).optional(),
  documentType: z.string().max(60).optional(),
  /** Selected or surrounding text the model should work on. */
  input: z.string().max(30000).optional(),
  instruction: z.string().max(4000).optional(),
  tone: z.enum(['formal', 'plain', 'concise']).default('formal'),
});

/* ── Types ────────────────────────────────────────────────────────────── */

export type RegisterInput = z.infer<typeof registerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type CreateDocumentInput = z.infer<typeof createDocumentSchema>;
export type SaveDraftInput = z.infer<typeof saveDraftSchema>;
export type PublishInput = z.infer<typeof publishSchema>;
export type UpsertTemplateInput = z.infer<typeof upsertTemplateSchema>;
export type CreateChangeRequestInput = z.infer<typeof createChangeRequestSchema>;
export type UpdateChangeRequestInput = z.infer<typeof updateChangeRequestSchema>;
export type CommentInput = z.infer<typeof commentSchema>;
export type DocumentMetadata = z.infer<typeof documentMetadataSchema>;
export type ReviewChangeRequestInput = z.infer<typeof reviewChangeRequestSchema>;
export type MergeChangeRequestInput = z.infer<typeof mergeChangeRequestSchema>;
export type ApprovalPolicyInput = z.infer<typeof approvalPolicySchema>;
export type UpsertPdfThemeInput = z.infer<typeof upsertPdfThemeSchema>;
export type AiAssistInput = z.infer<typeof aiAssistSchema>;
export type AiImportInput = z.infer<typeof aiImportSchema>;
export type AiDraftInput = z.infer<typeof aiDraftSchema>;
export type ListDocumentsQuery = z.infer<typeof listDocumentsQuerySchema>;
export type TemplateFieldInput = z.infer<typeof templateFieldSchema>;
